import { useEffect, useMemo, useState } from 'react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AGENT_FONT_CATALOG,
  DEFAULT_AGENT_FONT_ID,
  getAgentFontCssFamily,
  resolveAgentFont,
} from '../../../../widget/src/agent/fontCatalog';
import { loadFont } from '../../../../widget/src/agent/fontLoader';

export default function FontDropdown({
  value,
  setValue,
}: {
  value: string | undefined;
  setValue: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const currentValue = value || DEFAULT_AGENT_FONT_ID;
  const currentFont = resolveAgentFont(currentValue);

  useEffect(() => {
    void loadFont(currentFont.id);
  }, [currentFont.id]);

  const sortedFonts = useMemo(
    () =>
      [...AGENT_FONT_CATALOG].sort((a, b) =>
        a.family.localeCompare(b.family),
      ),
    [],
  );

  return (
    <div className="w-full max-w-72 mt-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            <span
              className="block truncate text-left"
              style={{ fontFamily: getAgentFontCssFamily(currentFont.id) }}
            >
              {currentFont.family}
            </span>
            <ChevronUpDownIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <Command>
            <CommandInput placeholder="Search fonts..." />
            <CommandList>
              <CommandEmpty>No fonts found.</CommandEmpty>
              {sortedFonts.map((font) => (
                <CommandItem
                  key={font.id}
                  value={`${font.family} ${font.category} ${font.id}`}
                  className="cursor-pointer"
                  onSelect={() => {
                    setValue(font.id);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={`mr-2 h-4 w-4 ${
                      font.id === currentFont.id ? 'opacity-100' : 'opacity-0'
                    }`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <div
                      className="truncate"
                      style={{ fontFamily: getAgentFontCssFamily(font.id) }}
                    >
                      {font.family}
                    </div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {font.category}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
